/**
 * The positions a pack Pokémon is listed for (slice 11), from its base stats. Position fit
 * (packages/sim matchup.ts `positionFit`) reads the list: out of it, a Pokémon plays at its slot
 * family's multiplier. The screens are balance.json `positionFit.listing.pairs`: a Pokémon lists
 * every pair whose screen it passes; passing none, it lists the one pair it comes closest to (the
 * best worst ratio of stat to minimum), so the list is never empty. An empty list would mean
 * "fits anywhere", the opposite of a Pokémon that passes nothing. Pure.
 */
import type { Position, Stats } from '@tdmons/sim';
import balanceJson from '../balance.json';

type Balance = typeof balanceJson;

/** A screen's stat: a battle stat, or `atkDef` for avg(Atk, Def) (the block strength). */
type ScreenStat = 'atk' | 'def' | 'spa' | 'spd' | 'spe' | 'atkDef';

interface Pair {
  positions: Position[];
  min: Partial<Record<ScreenStat, number>>;
}

function statOf(base: Stats, s: ScreenStat): number {
  return s === 'atkDef' ? (base.atk + base.def) / 2 : base[s];
}

/** The pair's worst ratio of stat to minimum: 1 or more passes the screen. */
function margin(base: Stats, pair: Pair): number {
  let worst = Infinity;
  for (const [s, min] of Object.entries(pair.min) as [ScreenStat, number][]) {
    worst = Math.min(worst, statOf(base, s) / min);
  }
  return worst;
}

export function listedPositions(base: Stats, b: Balance = balanceJson): Position[] {
  const pairs = b.positionFit.listing.pairs as Pair[];
  const passed = pairs.filter((p) => margin(base, p) >= 1);
  let chosen = passed;
  if (chosen.length === 0) {
    let best: Pair | undefined;
    let top = -Infinity;
    for (const p of pairs) {
      const m = margin(base, p);
      if (m > top) {
        best = p;
        top = m;
      }
    }
    chosen = best ? [best] : [];
  }
  const out: Position[] = [];
  for (const p of chosen) for (const pos of p.positions) if (!out.includes(pos)) out.push(pos);
  return out;
}
